import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { MOBILE_VERSION_SELECTOR, DESKTOP_VERSION_SELECTOR } from 'elc-universal-utils';
import { Breakpoints } from 'elc-base-theme';
import { removeAllNodesBySelector, cleanseDOM } from './utils/DOMCleanser';
import { runPerformanceMarker } from './utils/PerformanceMarker';
import { ComponentRenderer, ComponentNodeType } from './renderers';

const COMPONENT_SELECTOR = '[data-component]';

interface IRenderParams {
    component: React.ReactElement;
    node: HTMLElement;
    isSSR: boolean;
    name: string;
}

export class BrowserComponentRenderer extends ComponentRenderer {
    private nodesToRenderList: Set<HTMLElement> = new Set([]);
    private isRenderReadyPromise: Promise<void>;
    public isRenderReady = () => this.isRenderReadyPromise;

    protected getNodeAttribute = (node: HTMLElement, name: string) => node.getAttribute(name);

    private isElement = (node: Node) => node.nodeType === Node.ELEMENT_NODE;

    private isServiceViewNode = (element: HTMLElement): boolean =>
        element.matches(COMPONENT_SELECTOR);

    private getServiceViewsFromNode = (node: HTMLElement) =>
        node.querySelectorAll(COMPONENT_SELECTOR);

    private addNodeToRenderList = (nodes: Set<HTMLElement>, nodeToAdd: HTMLElement) => {
        if (!this.nodesToRenderList.has(nodeToAdd)) {
            nodes.add(nodeToAdd);
            this.nodesToRenderList.add(nodeToAdd);
        }
    };

    private removeNodeFromRenderList = (nodeToRemove: HTMLElement) => {
        if (this.nodesToRenderList.has(nodeToRemove)) {
            this.nodesToRenderList.delete(nodeToRemove);
        }
    };

    private processMutationRecord = (nodes: Set<HTMLElement>, record: MutationRecord) => {
        record.addedNodes.forEach((node: HTMLElement) => {
            if (this.isElement(node)) {
                const serviceViews = this.isServiceViewNode(node)
                    ? [node]
                    : this.getServiceViewsFromNode(node);

                serviceViews.forEach((view: HTMLElement) => this.addNodeToRenderList(nodes, view));
            }
        });

        // If a ServiceView is removed from DOM we need to remove also the reference to it
        record.removedNodes.forEach(this.removeNodeFromRenderList);

        return nodes;
    };

    private onHTMLChanged = async (records: MutationRecord[]) => {
        const addedNodes = records.reduce(this.processMutationRecord, new Set<HTMLElement>());

        await this.renderReactNodes([...addedNodes]);
    };

    private createMutationObserver = (callback: MutationCallback) => {
        if (typeof MutationObserver !== 'undefined') {
            return new MutationObserver(callback);
        }

        return null;
    };

    private watchRenderOutput() {
        const mutationObserverComponent = this.createMutationObserver(this.onHTMLChanged);
        if (mutationObserverComponent) {
            mutationObserverComponent.observe(document.body, {
                subtree: true,
                childList: true
            });
        }
    }

    private render = ({ component, node, isSSR, name }: IRenderParams) => {
        if (isSSR) {
            ReactDOM.hydrate(component, node, () => {
                runPerformanceMarker({
                    markArgument: `get_view_ssr_end_${name}`,
                    measureArguments: [
                        `hydrate_time_${name}`,
                        `get_view_${name}`,
                        `get_view_ssr_end_${name}`
                    ]
                });
            });
        } else {
            ReactDOM.render(component, node, () => {
                runPerformanceMarker({
                    markArgument: `get_view_end_${name}`,
                    measureArguments: [
                        `render_time_${name}`,
                        `get_view_${name}`,
                        `get_view_end_${name}`
                    ]
                });
            });
        }
    };

    private renderReactNodes = async (nodes: HTMLElement[]) => {
        await Promise.all(
            nodes.map(async (node: HTMLElement) => {
                const nodeToRender: ComponentNodeType | null = await this.getComponentForNode(
                    this.convertNodeToComponentNodeType(node)
                );

                if (nodeToRender && nodeToRender.component) {
                    const { component, state, name } = nodeToRender;
                    this.render({
                        component,
                        node: nodeToRender.node,
                        isSSR: Boolean(state),
                        name
                    });
                }

                return Promise.resolve();
            })
        );

        return Promise.resolve();
    };

    private renderLazyNode = (node: HTMLElement) => {
        if (!this.nodesToRenderList.has(node)) {
            this.nodesToRenderList.add(node);
            this.renderReactNodes([node]);
        }
    };

    private processMountPoints = async () => {
        const allNodes: NodeListOf<HTMLElement> = document.querySelectorAll(COMPONENT_SELECTOR);

        const toRenderNodes = [...allNodes].filter(this.shouldRenderNode);
        this.nodesToRenderList = new Set(toRenderNodes);

        await this.renderReactNodes(toRenderNodes);

        if (toRenderNodes.length < allNodes.length) {
            this.initLazyRenderer(this.renderLazyNode);
        }
    };

    private onDOMContentLoaded = () => {
        this.isRenderReadyPromise = this.processMountPoints();
        // ServiceViews up from 5.x doesn't depend on the Component Renderer anymore
        // We could remove the MutationObserver if we could upgrade all brands to use this newer version
        this.watchRenderOutput();
    };

    private cleanUpMarkup = () => {
        const isDesktop = window.matchMedia(`(min-width: ${Breakpoints.desktop}px)`).matches;
        if (isDesktop) {
            removeAllNodesBySelector(`.${MOBILE_VERSION_SELECTOR}`);
            cleanseDOM(`.${DESKTOP_VERSION_SELECTOR}`);
        } else {
            removeAllNodesBySelector(`.${DESKTOP_VERSION_SELECTOR}`);
            cleanseDOM(`.${MOBILE_VERSION_SELECTOR}`);
        }
    };

    public init = () => {
        const isDocumentReady = ['complete', 'interactive'].includes(document.readyState);
        this.cleanUpMarkup();

        if (isDocumentReady) {
            this.onDOMContentLoaded();
        } else {
            document.addEventListener('DOMContentLoaded', this.onDOMContentLoaded);
        }
    };
}
